import './bootstrap'
import '../css/app.css'

// Import GTM Performance Optimizer
import './gtm-optimizer'


import { createApp, h } from 'vue'
import {createInertiaApp, router, usePage} from '@inertiajs/vue3'
import { resolvePageComponent } from 'laravel-vite-plugin/inertia-helpers'
import { ZiggyVue } from 'ziggy-js';
import VueTailwindDatepicker from 'vue-tailwind-datepicker'
import { createPinia } from 'pinia'
/* import the fontawesome core */
import { library } from '@fortawesome/fontawesome-svg-core'
/* import font awesome icon component */
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
// import Layout from '@/Layouts/MasterLayout.vue'
import Layout from '@/Layouts/MasterLayoutV2.vue'
/* @fortawesome/pro-solid-svg-icons fas*/
import {
    faEllipsisVertical,
    faPlay,
    faChevronDown as fasChevronDown,
    faChevronUp as fasChevronUp,
    faArrowLeft as fasArrowLeft,
    faLock as fasLock,
    faShare as fasShare,
    faCirclePlay as fasCirclePlay,
    faExpand as fasExpand,
    faSearch as fasSearch,
    faFlag as fasFlag,
    faCommentSms as fasCommentSms,
    faCopy as fasCopy,
    faXmark as fasXmark,
    faRightToBracket as fasRightToBracket,
    faCircleInfo as fasCircleInfo,
    faFilm,
    faArrowLeftFromBracket,
    faHeart as fasHeart,
    faStar as fasStar
} from '@fortawesome/pro-solid-svg-icons'
/* @fortawesome/pro-regular-svg-icons */
import {
    faChevronDown,
    faChevronUp,
    faVolumeSlash,
    faVolume,
    faCheck,
    faSparkles,
} from '@fortawesome/pro-regular-svg-icons'

/* @fortawesome Brands */
import {
    faTelegram,
    faWhatsapp,
    faReddit,
    faXTwitter,
} from '@fortawesome/free-brands-svg-icons'
import PrimeVue from 'primevue/config'
import Tailwind from 'primevue/passthrough/tailwind'
import VueDatePicker from '@vuepic/vue-datepicker'
import Bugsnag from '@bugsnag/js'
import BugsnagPluginVue from '@bugsnag/plugin-vue'
import BugsnagPerformance from '@bugsnag/browser-performance'

/* add icons to the library */
library.add(
    faEllipsisVertical,
    faPlay,
    faChevronDown,
    fasChevronDown,
    faChevronUp,
    fasChevronUp,
    fasArrowLeft,
    fasLock,
    fasShare,
    fasCirclePlay,
    faVolumeSlash,
    faVolume,
    fasExpand,
    fasSearch,
    fasFlag,
    fasCommentSms,
    fasCopy,
    faTelegram,
    faReddit,
    faXTwitter,
    faWhatsapp,
    fasXmark,
    fasRightToBracket,
    faCheck,
    faSparkles,
    fasCircleInfo,
    faFilm,
    faArrowLeftFromBracket,
    fasHeart,
    fasStar
)

const appName = window.document.getElementsByTagName('title')[0]?.innerText || 'Laravel'
const pinia = createPinia()

// Debug: Log the app name
console.log('App name:', appName)

const fontURL = 'https://use.typekit.net/zut8xvm.css'
const link = document.createElement('link')
link.rel = 'stylesheet'
link.href = fontURL
document.head.appendChild(link)

// Initialize Bugsnag
if (import.meta.env.VITE_BUGSNAG_API_KEY) {
    Bugsnag.start({
        apiKey: import.meta.env.VITE_BUGSNAG_API_KEY,
        plugins: [new BugsnagPluginVue()],
        enabledReleaseStages: ['production'],
        releaseStage: import.meta.env.VITE_APP_ENV,
        onError: (event) => {
            // Add navigation context
            event.addMetadata('navigation', {
                url: window.location.href,
                path: window.location.pathname,
                referrer: document.referrer || null,
                userAgent: navigator.userAgent,
                online: navigator.onLine,
                visibility: document.visibilityState,
            })

            // Enrich Axios errors with request/response details
            const originalError = event.originalError
            if (originalError?.isAxiosError) {
                const { config, response, message } = originalError
                event.addMetadata('axios_request', {
                    method: config?.method?.toUpperCase(),
                    url: config?.url,
                    baseURL: config?.baseURL,
                    timeout: config?.timeout,
                    params: config?.params,
                })
                if (response) {
                    const body = typeof response.data === 'string'
                        ? response.data.substring(0, 1000)
                        : JSON.stringify(response.data)?.substring(0, 1000) || null
                    event.addMetadata('axios_response', {
                        status: response.status,
                        statusText: response.statusText,
                        body: body,
                    })
                } else {
                    event.addMetadata('axios_response', {
                        message: message,
                        code: originalError.code,
                    })
                }
            }
        },
    })
    BugsnagPerformance.start({ apiKey: import.meta.env.VITE_BUGSNAG_API_KEY })
}

createInertiaApp({
    title: title => {
        const fullTitle = `${title} - ${appName}`
        return fullTitle
    },
    resolve: name =>
        resolvePageComponent(`./Pages/${name}.vue`, import.meta.glob('./Pages/**/*.vue')).then(
            page => {
                page.default.layout = page.default.layout || Layout

                return page
            },
        ),
    setup({ el, App, props, plugin }) {
        const app = createApp({ render: () => h(App, props) })
            .use(plugin)
            .use(ZiggyVue)
            .use(VueTailwindDatepicker)
            .use(pinia)
            .use(PrimeVue, {
                unstyled: true,
                pt: Tailwind,
            })
            .use(Bugsnag.getPlugin('vue'))
            .component('font-awesome-icon', FontAwesomeIcon)
            .component('VueDatePicker', VueDatePicker)

        // Global right-click handler (production only)
        app.config.globalProperties.$disableRightClick = (event) => {
            try {
                const isProduction = props?.initialPage?.props?.app?.env === 'production'
                if (isProduction) {
                    if (event && typeof event.preventDefault === 'function') {
                        event.preventDefault()
                    }
                    return false
                }
            } catch (e) {
                // no-op
            }
            return true
        }

        app.mount(el)
    },
    progress: {
        // The delay after which the progress bar will appear, in milliseconds...
        delay: 250,

        // The color of the progress bar...
        color: '#0052cc',

        // Whether to include the default NProgress styles...
        includeCSS: true,

        // Whether the NProgress spinner will be shown...
        showSpinner: false,
    },
})

// Global event listener for router error handling
router.on('invalid', (event) => {
    console.log('Invalid Inertia Response Received:', event.detail.response);

    const page = usePage();
    const isProduction = page.props?.app?.env === 'production' ||
        page.props?.app?.isProduction === true;

    // Prevent the default modal in production only
    if (isProduction) {
        if (import.meta.env.VITE_BUGSNAG_API_KEY) {
            const response = event.detail.response;
            Bugsnag.notify(new Error('Invalid Inertia Response Received'), function (bugsnagEvent) {
                bugsnagEvent.severity = 'error';
                bugsnagEvent.addMetadata('request', {
                    url: window.location.href,
                });
                const body = typeof response?.data === 'string'
                    ? response.data.substring(0, 1000)
                    : JSON.stringify(response?.data)?.substring(0, 1000) || null;
                bugsnagEvent.addMetadata('response', {
                    status: response?.status,
                    statusText: response?.statusText,
                    headers: response?.headers || {},
                    body: body,
                });
            })
        }
        event.preventDefault();
    }

    window.dispatchEvent(new CustomEvent('show-toast', {
        detail: {
            message: 'An unexpected error occurred. Please try again.',
            icon: 'fas fa-circle-info',
            duration: 5000
        }
    }))
});

axios.interceptors.response.use(
    response => response,
    error => {
        if (error.response?.status === 401) {
            const intendedUrl = window.location.pathname + window.location.search;

            window.location.href = '/login' + intendedUrl ? '?dest=' + intendedUrl : '';
        }
        return Promise.reject(error);
    }
);
